<!--
  Tab strip: synthetic general tab fixed first; DnD reorder among real tabs; rename via double-click or menu.
-->
<script setup lang="ts">
import { nextTick, ref } from 'vue'
import type { BuilderTab } from '../model/types'

const TAB_DRAG_MIME = 'application/x-passport-builder-tab-id'

defineProps<{
  tabs: BuilderTab[]
  activeTabId: string
}>()

const emit = defineEmits<{
  'update:activeTabId': [id: string]
  'add-tab': []
  'remove-tab': [id: string]
  'rename-tab': [id: string, newLabel: string]
  'reorder-tabs': [draggedTabId: string, targetTabId: string]
}>()

const editingTabId = ref<string | null>(null)
const renameDraft = ref('')
const dragTabId = ref<string | null>(null)
const dropTargetTabId = ref<string | null>(null)
const dropSide = ref<'left' | 'right'>('left')

async function startRename(tab: BuilderTab) {
  if (!tab.renamable) return
  editingTabId.value = tab.id
  renameDraft.value = tab.label
  await nextTick()
  const root = document.getElementById(`pb-tab-rename-${tab.id}`)
  const input = root?.querySelector('input') as HTMLInputElement | null
  input?.focus()
  input?.select()
}

function commitRename(tab: BuilderTab) {
  if (editingTabId.value !== tab.id) return
  const next = renameDraft.value.trim()
  editingTabId.value = null
  if (next && next !== tab.label) {
    emit('rename-tab', tab.id, next)
  }
}

function cancelRename(tab: BuilderTab) {
  if (editingTabId.value !== tab.id) return
  editingTabId.value = null
  renameDraft.value = tab.label
}

function onTabDragStart(tab: BuilderTab, e: DragEvent) {
  if (!tab.draggable || editingTabId.value === tab.id) {
    e.preventDefault()
    return
  }
  dragTabId.value = tab.id
  e.dataTransfer?.setData(TAB_DRAG_MIME, tab.id)
  e.dataTransfer!.effectAllowed = 'move'
}

function onTabDragEnd() {
  dragTabId.value = null
  dropTargetTabId.value = null
}

function onTabDragOver(tab: BuilderTab, e: DragEvent) {
  if (!tab.draggable || tab.isSynthetic) return
  e.preventDefault()
  e.dataTransfer!.dropEffect = 'move'
  dropTargetTabId.value = tab.id
  const rect = (e.currentTarget as HTMLElement).getBoundingClientRect()
  dropSide.value = e.clientX < rect.left + rect.width / 2 ? 'left' : 'right'
}

function onTabDragLeave(tab: BuilderTab) {
  if (dropTargetTabId.value === tab.id) {
    dropTargetTabId.value = null
  }
}

function onTabDrop(target: BuilderTab, e: DragEvent) {
  e.preventDefault()
  const raw = e.dataTransfer?.getData(TAB_DRAG_MIME) || dragTabId.value
  dragTabId.value = null
  dropTargetTabId.value = null
  if (!raw || target.isSynthetic || !target.draggable || raw === target.id) return
  emit('reorder-tabs', raw, target.id)
}
</script>

<template>
  <div class="pb-tab-bar">
    <div
      v-for="tab in tabs"
      :key="tab.id"
      class="pb-tab"
      :class="{
        'pb-tab--active': tab.id === activeTabId,
        'pb-tab--draggable': tab.draggable,
        'pb-tab--dragging': dragTabId === tab.id,
        'pb-tab--drop-left': dropTargetTabId === tab.id && dropSide === 'left' && dragTabId !== tab.id,
        'pb-tab--drop-right': dropTargetTabId === tab.id && dropSide === 'right' && dragTabId !== tab.id,
      }"
      :draggable="Boolean(tab.draggable) && editingTabId !== tab.id"
      @click="emit('update:activeTabId', tab.id)"
      @dragstart="onTabDragStart(tab, $event)"
      @dragend="onTabDragEnd"
      @dragover="onTabDragOver(tab, $event)"
      @dragleave="onTabDragLeave(tab)"
      @drop="onTabDrop(tab, $event)"
    >
      <VTextField
        v-if="editingTabId === tab.id"
        :id="'pb-tab-rename-' + tab.id"
        v-model="renameDraft"
        class="pb-tab-rename-field"
        variant="plain"
        density="compact"
        hide-details
        single-line
        @keydown.enter.prevent="commitRename(tab)"
        @keydown.esc.prevent="cancelRename(tab)"
        @blur="commitRename(tab)"
        @click.stop
        @dragstart.stop.prevent
      />
      <span
        v-else
        class="pb-tab-label"
        @dblclick.stop="startRename(tab)"
      >
        {{ tab.isSynthetic ? $t('itf.passport_builder_general_tab') : tab.label }}
      </span>
      <span
        v-if="tab.closable"
        class="pb-tab-close material-symbols-rounded"
        role="button"
        :aria-label="$t('itf.close_tab')"
        @click.stop.prevent="emit('remove-tab', tab.id)"
      >close</span>
    </div>
    <span
      class="pb-tab-add material-symbols-rounded"
      role="button"
      :aria-label="$t('itf.add_tab')"
      @click="emit('add-tab')"
    >add</span>
  </div>
</template>

<style scoped lang="scss">
.pb-tab-rename-field {
  width: 140px;
  min-width: 100px;
  max-height: 28px;

  :deep(.v-field) {
    --v-field-padding-top: 0;
    --v-field-padding-bottom: 0;
  }

  :deep(.v-field__input) {
    font-size: 13px;
    padding-top: 2px;
    padding-bottom: 2px;
    min-height: 24px;
    max-height: 28px;
    line-height: 1;
  }
}
</style>
